// TaxonomyAdmin — one admin page for every small admin-editable list.
//
// WHY IT IS GENERIC. Vendor types and item kinds are the same object: a short
// key the database stores, a label people read and rename freely, a sort order,
// an active flag, and a count of the rows wearing it. They also want the same
// two rules — retire rather than delete, and never let a key change — and those
// rules are the reason the CRUD is safe to hand to a user at all. Written twice
// they drift; the second page is where somebody forgets that a refused save has
// to put the old value back.
//
// A page supplies a config object and, when its create form needs more than a
// name and an order, its own form component. Everything else is here.
//
// KEY IS IDENTITY, LABEL IS WHAT PEOPLE SEE. Rows attach by the key, so a label
// is renamed at will and a key never changes: the API refuses a key edit and
// this page never offers one. Retiring is is_active; deleting is only possible
// while nothing carries the key, which is why every row shows its count.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import { DataTable } from '../components/table/DataTable';
import PageHeader, { PrimaryAction } from '../components/PageHeader';
import { buttonClass } from '../components/ui/Button';
import { Field } from '../components/ui/Field';
import Tag from '../components/ui/Tag';
import Tooltip from '../components/ui/Tooltip';
import Dialog from '../components/ui/Dialog';
import EmptyState from '../components/ui/EmptyState';

/** The shape every taxonomy row shares. Pages extend it. */
export interface TaxonomyRow {
  key: string;
  label: string;
  sort_order: number;
  is_active: boolean;
}

/** Props a page's own create form receives. `onCreated` hands back the created
 *  row so a caller can select it; the page then closes and reloads. */
export interface TaxonomyFormProps<Row extends TaxonomyRow> {
  onClose: () => void;
  onCreated: (row: Row) => void;
}

export interface TaxonomyConfig<Row extends TaxonomyRow> {
  /** The <h1>. */
  title: string;
  /** Collection path for useApi — no leading /api, it is already in the base. */
  endpoint: string;
  /** Singular, lower case: "vendor type". Heads the label column, capitalized. */
  noun: string;
  /** Plural, lower case: "vendor types". Used in help and load errors. */
  nounPlural: string;
  /** Field on the row holding how many things carry this key. */
  countField: string;
  countHeader: string;
  /** HelpDrawer body. */
  help: React.ReactNode;
  keyColumn: {
    header: string;
    /** Name → key, for the built-in create form. */
    transform: (label: string) => string;
    placeholder: string;
    /** One line under the key field explaining what it is. */
    hint: React.ReactNode;
  };
  newButtonLabel: string;
  /** Submit button on the built-in create form. */
  submitLabel: string;
  deleteBlockedTitle: (count: number) => string;
  /**
   * Columns unique to this taxonomy. They land between Key and Order, because
   * that is where a row's own properties read as properties rather than as
   * bookkeeping. Module scope like the shared ones: never build them inline.
   */
  extraColumns?: ColumnDef<Row, any>[];
  /** Supply one when the create form needs fields the built-in form has not.
   *  Absent means the built-in name/key/order modal. */
  NewRowForm?: React.ComponentType<TaxonomyFormProps<Row>>;
  /** Called with every freshly loaded list, for a page that also caches it. */
  onLoaded?: (rows: Row[]) => void;
  /** Shell width. Extra columns usually want more than the default. */
  width?: string;
}

/** What a cell cannot derive from its own row, read at render time off `meta`. */
export interface TaxonomyMeta<Row extends TaxonomyRow> {
  onRename: (row: Row, label: string) => Promise<boolean>;
  onReorder: (row: Row, sortOrder: string) => Promise<boolean>;
  onToggleActive: (row: Row) => void;
  onDelete: (row: Row) => void;
  /** For extra columns: PATCH any field, reload, toast, revert-on-refusal. */
  onPatch: (row: Row, body: Record<string, unknown>, done: string) => Promise<boolean>;
}

// A cell that saves on blur and REVERTS on a refused save, the same contract the
// part-families Kind select holds: an optimistic value left sitting over a
// database that disagrees is the one outcome worse than an error toast.
export const EditableText: React.FC<{
  value: string;
  onSave: (next: string) => Promise<boolean>;
  ariaLabel: string;
  className?: string;
  numeric?: boolean;
}> = ({ value, onSave, ariaLabel, className = '', numeric }) => {
  const [draft, setDraft] = useState(String(value));
  const [saving, setSaving] = useState(false);

  // The row can be refreshed under us by a sibling edit, so the committed value
  // wins whenever it changes and we are not mid-edit.
  useEffect(() => { setDraft(String(value)); }, [value]);

  const commit = async () => {
    if (saving || draft === String(value)) return;
    setSaving(true);
    const ok = await onSave(draft);
    if (!ok) setDraft(String(value));
    setSaving(false);
  };

  return (
    <Field
      variant="inline"
      aria-label={ariaLabel}
      type={numeric ? 'number' : 'text'}
      value={draft}
      disabled={saving}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') setDraft(String(value));
      }}
      className={className}
    />
  );
};

const capitalize = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s);

/**
 * The columns, built once per config.
 *
 * Per the DataTable contract the array must never change identity while the page
 * is mounted, and the actions a cell cannot derive from its own row ride on
 * `meta`. A config object is a module constant on every page here, so a useMemo
 * keyed on it is as stable as the old module-scope array was. Never memoize one
 * of these cells.
 */
function buildColumns<Row extends TaxonomyRow>(config: TaxonomyConfig<Row>): ColumnDef<Row, any>[] {
  const count = (row: Row) => Number((row as any)[config.countField] ?? 0);
  return [
    {
      id: 'label',
      header: capitalize(config.noun),
      meta: { cellClass: 'text-on-surface' },
      cell: ({ row, table }) => {
        const meta = table.options.meta as unknown as TaxonomyMeta<Row>;
        return (
          <EditableText
            value={row.original.label}
            ariaLabel={`Rename ${row.original.label}`}
            onSave={(next) => meta.onRename(row.original, next)}
          />
        );
      },
    },
    {
      id: 'key',
      header: config.keyColumn.header,
      meta: { cellClass: 'font-mono text-xs text-on-surface-variant whitespace-nowrap' },
      // Not editable, and shown anyway: it is what the database stores and what
      // an import or a script would have to say, so hiding it would make a
      // support question unanswerable.
      cell: ({ row }) => row.original.key,
    },
    ...(config.extraColumns || []),
    {
      id: 'sort_order',
      header: 'Order',
      meta: { cellClass: 'text-on-surface-variant tabular-nums w-20' },
      cell: ({ row, table }) => {
        const meta = table.options.meta as unknown as TaxonomyMeta<Row>;
        return (
          <EditableText
            numeric
            value={String(row.original.sort_order)}
            ariaLabel={`Sort order for ${row.original.label}`}
            className="tabular-nums"
            onSave={(next) => meta.onReorder(row.original, next)}
          />
        );
      },
    },
    {
      id: 'count',
      header: config.countHeader,
      meta: { cellClass: 'text-on-surface-variant tabular-nums' },
      cell: ({ row }) => count(row.original),
    },
    {
      id: 'active',
      header: 'Status',
      cell: ({ row, table }) => {
        const meta = table.options.meta as unknown as TaxonomyMeta<Row>;
        const t = row.original;
        return (
          <Tag
            mono
            tone={t.is_active ? 'accent' : 'neutral'}
            onClick={() => meta.onToggleActive(t)}
          >
            {t.is_active ? 'active' : 'retired'}
          </Tag>
        );
      },
    },
    {
      id: 'remove',
      header: '',
      cell: ({ row, table }) => {
        const meta = table.options.meta as unknown as TaxonomyMeta<Row>;
        const t = row.original;
        const n = count(t);
        const inUse = n > 0;
        // A refusal says why. The tooltip hangs off the wrapper the kit makes for a
        // disabled control, which still takes the pointer and focus the button does not.
        return (
          <Tooltip label={inUse ? config.deleteBlockedTitle(n) : ''}>
            <button
              type="button"
              disabled={inUse}
              onClick={() => meta.onDelete(t)}
              className={buttonClass('danger-quiet', 'sm')}
            >
              delete
            </button>
          </Tooltip>
        );
      },
    },
  ];
}

function TaxonomyAdmin<Row extends TaxonomyRow>({ config }: { config: TaxonomyConfig<Row> }) {
  const api = useApi();
  const toast = useToast();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const columns = useMemo(() => buildColumns(config), [config]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // The collection lands under a key named for the resource — `types` here,
      // `kinds` there — so the array is found rather than named. A bare array
      // body works too, for a router that answers plainly.
      const res: any = await api.get(config.endpoint);
      const list: Row[] = Array.isArray(res)
        ? res
        : (Object.values(res || {}).find((v) => Array.isArray(v)) as Row[]) || [];
      setRows(list);
      config.onLoaded?.(list);
    } catch (e: any) {
      toast.error(e?.message || `Failed to load ${config.nounPlural}`);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  // `load` is stable: it closes over the page's config object, which is a module
  // constant. api and toast are deliberately out of its dep list, per the house
  // rule that those two are omitted on purpose.
  useEffect(() => { load(); }, [load]);

  const onPatch = async (row: Row, body: Record<string, unknown>, done: string) => {
    try {
      await api.patch(`${config.endpoint}/${encodeURIComponent(row.key)}`, { body });
      await load();
      toast.success(done);
      return true;
    } catch (e: any) {
      toast.error(e?.data?.error?.message || e?.message || 'That change was refused');
      return false;
    }
  };

  const onRename = (row: Row, label: string) => {
    const next = label.trim();
    if (!next) {
      toast.error(`A ${config.noun} needs a name`);
      return Promise.resolve(false);
    }
    return onPatch(row, { label: next }, 'Renamed');
  };

  const onReorder = (row: Row, sortOrder: string) => {
    const n = Number(sortOrder);
    if (!Number.isInteger(n) || n < 0) {
      toast.error('Order is a whole number');
      return Promise.resolve(false);
    }
    return onPatch(row, { sort_order: n }, 'Reordered');
  };

  const onToggleActive = (row: Row) => {
    onPatch(row, { is_active: !row.is_active }, row.is_active ? 'Retired' : 'Back in use');
  };

  const onDelete = async (row: Row) => {
    try {
      await api.delete(`${config.endpoint}/${encodeURIComponent(row.key)}`);
      await load();
      toast.success(`Deleted ${row.label}`);
    } catch (e: any) {
      toast.error(e?.data?.error?.message || e?.message || 'Delete was refused');
    }
  };

  const closeAndReload = async () => { setShowNew(false); await load(); };
  const NewRowForm = config.NewRowForm;

  return (
    <div className={`p-4 ${config.width || 'max-w-3xl'} mx-auto space-y-5`}>
      <PageHeader
        title={config.title}
        helpTitle={`About ${config.nounPlural}`}
        help={config.help}
        primary={
          <PrimaryAction onClick={() => setShowNew(true)}>{config.newButtonLabel}</PrimaryAction>
        }
      />

      <DataTable<Row>
        data={rows}
        columns={columns}
        getRowId={(r) => r.key}
        loading={loading}
        meta={{ onRename, onReorder, onToggleActive, onDelete, onPatch }}
        empty={<EmptyState variant="area">No {config.nounPlural} yet</EmptyState>}
      />

      {showNew && (
        NewRowForm
          ? <NewRowForm onClose={() => setShowNew(false)} onCreated={closeAndReload} />
          : <TaxonomyNewRowModal config={config} onClose={() => setShowNew(false)} onCreated={closeAndReload} />
      )}
    </div>
  );
}

/** The built-in create form: a name, the key it derives, and an order. */
function TaxonomyNewRowModal<Row extends TaxonomyRow>({
  config,
  onClose,
  onCreated,
}: { config: TaxonomyConfig<Row> } & TaxonomyFormProps<Row>) {
  const api = useApi();
  const toast = useToast();
  const [label, setLabel] = useState('');
  const [key, setKey] = useState('');
  const [keyTouched, setKeyTouched] = useState(false);
  const [sortOrder, setSortOrder] = useState('100');
  const [submitting, setSubmitting] = useState(false);

  // The key is derived from the name until somebody edits it, because a person
  // adding "Wire Harness" should not have to know what a key is. Once they touch
  // it, it is theirs.
  const effectiveKey = keyTouched ? key : config.keyColumn.transform(label);

  const submit = async () => {
    if (!label.trim() || !effectiveKey) {
      toast.error('A name is required');
      return;
    }
    setSubmitting(true);
    try {
      const created = await api.post(config.endpoint, {
        body: { key: effectiveKey, label: label.trim(), sort_order: Number(sortOrder) || 100 },
      });
      toast.success(`Added ${label.trim()}`);
      onCreated(created as Row);
    } catch (e: any) {
      toast.error(e?.data?.error?.message || e?.message || `Could not add that ${config.noun}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open
      title={`New ${config.noun}`}
      onClose={onClose}
      size="md"
      busy={submitting}
      dirty={label !== '' || (keyTouched && key !== '') || sortOrder !== '100'}
      footer={(
        <>
          <button onClick={onClose} className={buttonClass('quiet')}>
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting || !label.trim()}
            className={buttonClass('filled')}
          >
            {submitting ? 'Adding…' : config.submitLabel}
          </button>
        </>
      )}
    >
      <div className="space-y-4">
        <label className="block space-y-1">
          <span className="text-xs font-mono uppercase tracking-wider text-on-surface-variant">Name</span>
          <Field
            autoFocus
            value={label}
            placeholder="Wire harness"
            onChange={(e) => setLabel(e.target.value)}
          />
        </label>

        <label className="block space-y-1">
          <span className="text-xs font-mono uppercase tracking-wider text-on-surface-variant">Key</span>
          <Field
            className="font-mono"
            value={effectiveKey}
            placeholder={config.keyColumn.placeholder}
            onChange={(e) => { setKeyTouched(true); setKey(e.target.value); }}
          />
        </label>
        <p className="-mt-2 text-xs text-on-surface-variant">{config.keyColumn.hint}</p>

        <label className="block space-y-1">
          <span className="text-xs font-mono uppercase tracking-wider text-on-surface-variant">Order</span>
          <Field
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          />
        </label>
      </div>
    </Dialog>
  );
}

export default TaxonomyAdmin;
